/*
 * category-tabbings-arrows.js
 *
 * R14 (2026-05-03) — shared arrow-positioning script for the carousel
 * families A wants vertically centered on the FIRST card's image:
 *   1. .category_tabbings .product-carousel-container
 *      (PDP cr-carousel + PDP/home featured-products)
 *   2. .recently-viewed .recently-viewed__grid
 *      (PDP recently-viewed — buttons inserted dynamically by
 *      recently-viewed-carousel.js after products fetch + render)
 *
 * Why this exists:
 *   - Pre-R14, carousel-override.css :1200px hardcoded the prev/next arrows
 *     to top: 36% of the container, which landed near but not on the
 *     product image's vertical center on typical Arc Grove cards (image
 *     ~50% of card; remaining ~50% = title + price + judgeme + finish row).
 *     Cards with extra content (sale tag, multi-line title, finishing
 *     hover row) shifted that off further.
 *   - The cr-carousel section had inline JS (`positionArrows()`) that tried
 *     to compute imageCenterY dynamically, but it set element.style.top
 *     WITHOUT priority, so the stylesheet `top: 36% !important` won the
 *     cascade. The featured-products section had no positioning JS at all.
 *   - Recently-viewed had the SAME `top: 36%` hardcode at :1200px and no
 *     dynamic logic at all.
 *
 * Fix:
 *   1. carousel-override.css R14 deletes the 36% rule entirely.
 *   2. This script runs once per page, finds every matching carousel
 *      container per CONFIGS, measures the FIRST card's image-wrapper
 *      element, and sets the prev/next buttons' top to imageCenterY via
 *      setProperty('top', ..., 'important'). Combined with the existing
 *      transform: translateY(-50%) (still defined in carousel-override.css
 *      for .carousel-btn at every breakpoint), the arrow CENTER lands on
 *      the image CENTER.
 *   3. setProperty(..., 'important') guarantees JS wins the cascade
 *      against any future stylesheet !important rule.
 *
 * Public API:
 *   window.AGCarouselArrows.positionAll() — recently-viewed-carousel.js
 *   calls this after creating its prev/next buttons, since those buttons
 *   don't exist at DOMContentLoaded (cards + buttons are inserted by JS
 *   only after a /products/<handle>.js fetch resolves per stored handle).
 *
 * Lifecycle triggers (auto):
 *   - DOMContentLoaded (or immediate if document ready)
 *   - Each carousel image's `load` event (cold cache, lazy-loaded images)
 *   - window resize (breakpoint changes shift card heights)
 *   - ResizeObserver on first card's image-wrapper (catches aspect-ratio
 *     recalcs after lazy-load swaps or style changes)
 *
 * Mobile/tablet (<992px): arrows are hidden via existing
 * carousel-override.css media-query rules. Script bails early on small
 * viewports to skip the work.
 *
 * Idempotent: a global init flag guards against double-binding when the
 * script tag is emitted by multiple sections on the same page.
 */

(function () {
  'use strict';
  if (window.__agCategoryTabbingsArrowsInit) return;
  window.__agCategoryTabbingsArrowsInit = true;

  // Each config defines one carousel family's selector chain. Add new
  // families here to extend the script — no other code changes needed.
  var CONFIGS = [
    {
      // PDP cr-carousel + PDP/home featured-products
      container: '.category_tabbings .product-carousel-container',
      image: '.product-carousel-item .product-item_image-wrapper',
      prev: '.carousel-btn-prev',
      next: '.carousel-btn-next',
    },
    {
      // PDP recently-viewed — cards + buttons built dynamically by
      // recently-viewed-carousel.js. The first card's image lives at
      // .carousel-slide .rvp-card__image-wrap (different DOM from the
      // .product-carousel-item .product-item_image-wrapper used by
      // .category_tabbings).
      container: '.recently-viewed .recently-viewed__grid',
      image: '.carousel-slide .rvp-card__image-wrap',
      prev: '.carousel-prev',
      next: '.carousel-next',
    },
  ];

  function positionForConfig(cfg) {
    var containers = document.querySelectorAll(cfg.container);
    for (var i = 0; i < containers.length; i++) {
      var container = containers[i];
      var imageEl = container.querySelector(cfg.image);
      if (!imageEl) continue;
      var imageRect = imageEl.getBoundingClientRect();
      var containerRect = container.getBoundingClientRect();
      if (!imageRect.height) continue;
      var imageCenterY = (imageRect.top - containerRect.top) + (imageRect.height / 2);
      var prev = container.querySelector(cfg.prev);
      var next = container.querySelector(cfg.next);
      if (prev) prev.style.setProperty('top', imageCenterY + 'px', 'important');
      if (next) next.style.setProperty('top', imageCenterY + 'px', 'important');
    }
  }

  // Track which elements already have a ResizeObserver attached — WeakSet
  // so GC can reclaim entries whose elements get removed from the DOM.
  // Idempotent re-attach in positionAll() catches dynamically-inserted
  // image-wrappers (recently-viewed cards added by JS after page load).
  var observedEls = (typeof WeakSet !== 'undefined') ? new WeakSet() : null;

  function attachResizeObservers() {
    if (!window.ResizeObserver) return;
    for (var c = 0; c < CONFIGS.length; c++) {
      var cs = document.querySelectorAll(CONFIGS[c].container);
      for (var j = 0; j < cs.length; j++) {
        var first = cs[j].querySelector(CONFIGS[c].image);
        if (!first) continue;
        if (observedEls && observedEls.has(first)) continue;
        if (observedEls) observedEls.add(first);
        try { new ResizeObserver(scheduleReposition).observe(first); } catch (e) {}
      }
    }
  }

  function positionAll() {
    if (window.innerWidth < 992) return;
    for (var i = 0; i < CONFIGS.length; i++) positionForConfig(CONFIGS[i]);
    // Idempotent — re-attach observers on each call so newly-rendered
    // image-wrappers (e.g. recently-viewed cards inserted by JS after a
    // /products/<handle>.js fetch) get observed.
    attachResizeObservers();
  }

  // R14.3: rAF-coalesced reposition. window.resize fires many times during
  // a drag; we only need to position once per paint frame. requestAnimationFrame
  // also guarantees the browser has committed layout for the new viewport
  // size before we measure (vs running synchronously on the resize event,
  // where getBoundingClientRect may return mid-reflow values).
  var rafId = null;
  function scheduleReposition() {
    if (rafId) cancelAnimationFrame(rafId);
    rafId = requestAnimationFrame(function () {
      rafId = null;
      positionAll();
    });
  }

  // Public API. Recently-viewed-carousel.js calls this after dynamically
  // creating its prev/next buttons + cards (they don't exist at
  // DOMContentLoaded — they're appended after a fetch + render cycle).
  // Public method runs through scheduleReposition so callers also benefit
  // from the rAF-coalesce + post-layout measurement.
  window.AGCarouselArrows = { positionAll: scheduleReposition };

  function init() {
    positionAll();
    attachResizeObservers();

    // Re-position when each carousel image finishes loading (cold cache /
    // lazy-load case). Only adds load listeners to images that aren't
    // already complete to avoid leaking listeners on cached images.
    var imgs = document.querySelectorAll(
      '.category_tabbings .product-carousel-container img, ' +
      '.recently-viewed .recently-viewed__grid img'
    );
    for (var i = 0; i < imgs.length; i++) {
      if (!imgs[i].complete) {
        imgs[i].addEventListener('load', scheduleReposition, { once: true });
      }
    }

    // Coalesce resize via rAF — see scheduleReposition comment above.
    window.addEventListener('resize', scheduleReposition);

    // window.load fires after EVERYTHING (images, fonts, late-loaded JS) is
    // ready. Catches layout shifts caused by web fonts changing measured
    // metrics or images that didn't fire individual load events for some
    // reason.
    window.addEventListener('load', scheduleReposition);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init, { once: true });
  } else {
    init();
  }
})();
